import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Checkbox } from "../../components/ui/Checkbox";
import { Input } from "../../components/ui/Input";
import { Label } from "../../components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select";
import { Skeleton } from "../../components/ui/Skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/AlertDialog";
import {
  useAiConfig,
  useFetchModels,
  useTestProvider,
  useUpdateAiConfig,
  type AiModel,
  type AiProviderConfig,
  type AiProviderType,
  type ProviderCapabilities,
  type TestProviderResult,
} from "../../hooks/useAdminApi";
import {
  issueKey,
  parseServerIssue,
  slugifyId,
  toIssueMap,
  validateProviders,
} from "../../lib/providerValidation";

type ProviderForm = AiProviderConfig & {
  hasApiKey: boolean;
  apiKeyPreview: string;
  localKey: string;
  isNew: boolean;
};

const BASE_URL_PRESETS = [
  { label: "DeepSeek", value: "https://api.deepseek.com" },
  { label: "z.ai Coding Plan", value: "https://api.z.ai/api/coding/paas/v4" },
  { label: "OpenRouter", value: "https://openrouter.ai/api/v1" },
  { label: "Ollama local", value: "http://localhost:11434/v1" },
];

const CUSTOM_PRESET = "custom";

let localKeySeed = 0;
const nextLocalKey = () => {
  localKeySeed += 1;
  return `novo-${localKeySeed}`;
};

const emptyProvider = (): ProviderForm => ({
  id: "",
  name: "",
  type: "openai-compatible",
  baseURL: "",
  apiKey: "",
  model: "",
  enabled: true,
  priority: 1,
  supportsStructuredOutputs: false,
  hasApiKey: false,
  apiKeyPreview: "",
  localKey: nextLocalKey(),
  isNew: true,
});

const comparable = (providers: ProviderForm[]) =>
  JSON.stringify(
    providers.map(({ localKey: _localKey, isNew: _isNew, ...rest }, index) => ({
      ...rest,
      priority: index + 1,
    }))
  );

const parsePrice = (raw: string): number | undefined => {
  if (raw.trim().length === 0) return undefined;
  return Number(raw.replace(",", "."));
};

const priceTextKey = (localKey: string, field: "in" | "out") =>
  `${localKey}:${field}`;

const serverErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: string } | undefined;
    if (typeof body?.error === "string") return body.error;
  }
  return "Falha ao atualizar configuração";
};

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="text-xs text-destructive">{message}</p> : null;

const EFFORT_RANK: Record<string, number> = {
  low: 0,
  medium: 1,
  high: 2,
  max: 3,
};

const STRUCTURED_LABEL: Record<
  ProviderCapabilities["structuredOutputs"],
  string
> = {
  supported: "json_schema aceito pelo endpoint",
  rejected: "json_schema recusado pelo endpoint",
  unknown: "json_schema não conclusivo: o endpoint não respondeu ao teste",
};

const deliveryLabel = (caps: ProviderCapabilities): string => {
  switch (caps.reasoningDelivery) {
    case "separate":
      return "o raciocínio volta em campo separado do conteúdo, então não corrompe o JSON";
    case "inline":
      return "atenção: o raciocínio veio dentro do conteúdo e pode corromper o JSON do changelog";
    default:
      return "entrega do raciocínio não conclusiva: o modelo não raciocinou na sondagem";
  }
};

const reasoningLabel = (caps: ProviderCapabilities): string => {
  switch (caps.reasoning) {
    case "used":
      return `reasoning_effort aceito e usado em ${caps.maxEffort} (${caps.reasoningTokens} tokens de raciocínio)`;
    case "accepted":
      return `reasoning_effort aceito em ${caps.maxEffort}, mas o modelo não reportou tokens de raciocínio`;
    case "rejected":
      return "reasoning_effort recusado em todos os níveis";
    default:
      return "reasoning_effort não conclusivo: o endpoint não respondeu ao teste";
  }
};

const applyCapabilities = (
  provider: ProviderForm,
  caps: ProviderCapabilities
): Partial<ProviderForm> => {
  const changes: Partial<ProviderForm> = {};

  if (caps.structuredOutputs === "supported") {
    changes.supportsStructuredOutputs = true;
  } else if (caps.structuredOutputs === "rejected") {
    changes.supportsStructuredOutputs = false;
  }

  const effort = provider.reasoning?.effort ?? "high";

  if (caps.reasoning === "rejected") {
    changes.reasoning = { enabled: false, effort };
    return changes;
  }

  if (caps.reasoning !== "used" && caps.reasoning !== "accepted") {
    return changes;
  }

  const tooHigh =
    caps.maxEffort !== undefined &&
    EFFORT_RANK[effort] > EFFORT_RANK[caps.maxEffort];

  changes.reasoning = {
    enabled: caps.reasoning === "used" ? true : (provider.reasoning?.enabled ?? false),
    effort: tooHigh ? caps.maxEffort! : effort,
  };

  return changes;
};

export interface ProvidersTabProps {
  onDirtyChange?: (dirty: boolean) => void;
}

export const ProvidersTab: React.FC<ProvidersTabProps> = ({ onDirtyChange }) => {
  const { data, isLoading } = useAiConfig();
  const update = useUpdateAiConfig();
  const fetchModels = useFetchModels();
  const testProvider = useTestProvider();

  const [providers, setProviders] = useState<ProviderForm[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [modelsByKey, setModelsByKey] = useState<Record<string, AiModel[]>>({});
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [testResults, setTestResults] = useState<
    Record<string, TestProviderResult>
  >({});
  const [testingKey, setTestingKey] = useState<string | null>(null);
  const [loadingModelsKey, setLoadingModelsKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [priceText, setPriceText] = useState<Record<string, string>>({});

  const serverForm = useMemo<ProviderForm[] | undefined>(
    () =>
      data?.providers.map((provider) => ({
        ...provider,
        apiKey: "",
        localKey: provider.id,
        isNew: false,
      })),
    [data]
  );

  useEffect(() => {
    if (!serverForm) return;
    setProviders(serverForm);
    setPriceText({});
  }, [serverForm]);

  const isDirty = useMemo(
    () =>
      Boolean(serverForm) &&
      comparable(providers) !== comparable(serverForm ?? []),
    [providers, serverForm]
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const patch = (index: number, changes: Partial<ProviderForm>) => {
    setProviders((prev) =>
      prev.map((provider, i) => {
        if (i !== index) return provider;
        const merged = { ...provider, ...changes };
        if (changes.name !== undefined && provider.isNew) {
          const taken = prev
            .filter((_, other) => other !== index)
            .map((other) => other.id);
          merged.id = slugifyId(changes.name, taken);
        }
        return merged;
      })
    );
  };

  const move = (index: number, delta: number) => {
    setProviders((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeProvider = (index: number) => {
    setProviders((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLoadModels = async (index: number) => {
    const provider = providers[index];
    if (!(provider.apiKey || provider.hasApiKey) || !provider.baseURL) return;

    setLoadingModelsKey(provider.localKey);
    try {
      const models = await fetchModels.mutateAsync({
        id: provider.isNew ? undefined : provider.id,
        type: provider.type,
        baseURL: provider.baseURL,
        apiKey: provider.apiKey,
      });
      setModelsByKey((prev) => ({ ...prev, [provider.localKey]: models }));
    } catch {
      return;
    } finally {
      setLoadingModelsKey(null);
    }
  };

  const handleTest = async (index: number) => {
    const provider = providers[index];
    if (!(provider.apiKey || provider.hasApiKey) || !provider.baseURL) return;

    setTestingKey(provider.localKey);
    try {
      const result = await testProvider.mutateAsync({ provider });
      setTestResults((prev) => ({ ...prev, [provider.localKey]: result }));
      if (result.capabilities) {
        patch(index, applyCapabilities(provider, result.capabilities));
      }
    } catch {
      return;
    } finally {
      setTestingKey(null);
    }
  };

  const handleSave = async () => {
    const withIds = providers.map((provider, index) => ({
      ...provider,
      id: provider.id || slugifyId(provider.name, []),
      priority: index + 1,
    }));

    const issues = validateProviders(withIds);
    if (issues.length > 0) {
      setFieldErrors(toIssueMap(issues));
      const offender = withIds.find((provider) => provider.id === issues[0].id);
      if (offender) setExpanded(offender.localKey);
      toast.error("Corrija os campos destacados.");
      return;
    }

    setFieldErrors({});
    const payload: AiProviderConfig[] = withIds.map((provider) => ({
      id: provider.id,
      name: provider.name,
      type: provider.type,
      baseURL: provider.baseURL,
      apiKey: provider.apiKey,
      model: provider.model,
      enabled: provider.enabled,
      priority: provider.priority,
      supportsStructuredOutputs: provider.supportsStructuredOutputs ?? false,
      ...(provider.reasoning ? { reasoning: provider.reasoning } : {}),
      ...(provider.pricePerMTokenIn !== undefined
        ? { pricePerMTokenIn: provider.pricePerMTokenIn }
        : {}),
      ...(provider.pricePerMTokenOut !== undefined
        ? { pricePerMTokenOut: provider.pricePerMTokenOut }
        : {}),
    }));

    try {
      await update.mutateAsync({ providers: payload });
    } catch (error) {
      const message = serverErrorMessage(error);
      const issue = parseServerIssue(message);
      if (issue) {
        setFieldErrors(toIssueMap([issue]));
        const offender = withIds.find((provider) => provider.id === issue.id);
        if (offender) setExpanded(offender.localKey);
      }
      toast.error(message);
    }
  };

  const errorFor = (provider: ProviderForm, field: string) =>
    fieldErrors[issueKey(provider.id, field)];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        A ordem da lista é a ordem de tentativa: o primeiro habilitado é sempre o
        primeiro a ser chamado, e os seguintes entram só quando ele falha.
      </p>

      {providers.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-6 text-sm text-muted-foreground">
          Nenhum provider configurado. Adicione um para habilitar resumos com IA.
        </div>
      )}

      <div className="space-y-3">
        {providers.map((provider, index) => {
          const models = modelsByKey[provider.localKey] || [];
          const test = testResults[provider.localKey];
          const isOpen = expanded === provider.localKey;
          const preset = BASE_URL_PRESETS.find(
            (option) => option.value === provider.baseURL
          );

          return (
            <div
              key={provider.localKey}
              className="bg-card border border-border rounded-lg overflow-hidden"
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    aria-label={`Subir ${provider.name || "provider"}`}
                  >
                    <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === providers.length - 1}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    aria-label={`Descer ${provider.name || "provider"}`}
                  >
                    <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </div>

                <Checkbox
                  id={`enabled-${provider.localKey}`}
                  checked={provider.enabled}
                  onCheckedChange={(checked) =>
                    patch(index, { enabled: !!checked })
                  }
                  aria-label={`Habilitar ${provider.name || "provider"}`}
                />

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">
                    {provider.name || "Sem nome"}
                    <span className="ml-2 text-xs text-muted-foreground font-normal">
                      {index + 1}º na fila
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground font-mono truncate">
                    {provider.model || "modelo não definido"}
                  </p>
                </div>

                {test && (
                  <Badge tone={test.ok ? "success" : "danger"}>
                    {test.ok
                      ? `OK ${(test.latencyMs / 1000).toFixed(1)}s`
                      : "Falhou"}
                  </Badge>
                )}
                {!provider.enabled && <Badge tone="neutral">Desligado</Badge>}

                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setExpanded(isOpen ? null : provider.localKey)}
                >
                  {isOpen ? "Fechar" : "Editar"}
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-muted-foreground hover:text-destructive transition-colors p-2"
                      aria-label={`Remover ${provider.name || "provider"}`}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Remover {provider.name || "este provider"}?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        A remoção só vale depois de salvar a configuração. A
                        chave de API guardada para este provider será descartada.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => removeProvider(index)}>
                        Remover
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {isOpen && (
                <div className="border-t border-border px-4 py-5 grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor={`name-${provider.localKey}`}>Nome</Label>
                    <Input
                      id={`name-${provider.localKey}`}
                      value={provider.name}
                      onChange={(e) => patch(index, { name: e.target.value })}
                      placeholder="Ex: DeepSeek, OpenRouter, Ollama"
                      autoComplete="off"
                    />
                    <p className="text-xs text-muted-foreground font-mono">
                      id: {provider.id || "(definido ao salvar)"}
                    </p>
                    <FieldError message={errorFor(provider, "name")} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`type-${provider.localKey}`}>Tipo</Label>
                    <Select
                      value={provider.type}
                      onValueChange={(value) =>
                        patch(index, { type: value as AiProviderType })
                      }
                    >
                      <SelectTrigger id={`type-${provider.localKey}`}>
                        <SelectValue placeholder="Tipo do provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="openai-compatible">
                          OpenAI-compatible
                        </SelectItem>
                        <SelectItem value="anthropic">
                          Anthropic-compatible
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FieldError message={errorFor(provider, "type")} />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor={`baseurl-${provider.localKey}`}>
                      Base URL
                    </Label>
                    <div className="grid gap-2 md:grid-cols-[14rem_1fr]">
                      <Select
                        value={preset ? preset.value : CUSTOM_PRESET}
                        onValueChange={(value) =>
                          patch(index, {
                            baseURL: value === CUSTOM_PRESET ? "" : value,
                          })
                        }
                      >
                        <SelectTrigger
                          aria-label={`Preset de base URL de ${provider.name || "provider"}`}
                        >
                          <SelectValue placeholder="Preset" />
                        </SelectTrigger>
                        <SelectContent>
                          {BASE_URL_PRESETS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                          <SelectItem value={CUSTOM_PRESET}>Outro</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        id={`baseurl-${provider.localKey}`}
                        value={provider.baseURL || ""}
                        onChange={(e) => patch(index, { baseURL: e.target.value })}
                        placeholder="https://api.deepseek.com"
                        className="font-mono"
                        autoComplete="off"
                      />
                    </div>
                    <FieldError message={errorFor(provider, "baseURL")} />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor={`apikey-${provider.localKey}`}>
                      Chave de API
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id={`apikey-${provider.localKey}`}
                        type={showKeys[provider.localKey] ? "text" : "password"}
                        value={provider.apiKey}
                        placeholder={
                          provider.hasApiKey
                            ? `${provider.apiKeyPreview} (salva, deixe em branco para manter)`
                            : "Cole a chave de API"
                        }
                        onChange={(e) => patch(index, { apiKey: e.target.value })}
                        className="font-mono flex-1"
                        autoComplete="new-password"
                        spellCheck={false}
                      />
                      {provider.apiKey.length > 0 && (
                        <Button
                          type="button"
                          variant="secondary"
                          className="px-3"
                          onClick={() =>
                            setShowKeys((prev) => ({
                              ...prev,
                              [provider.localKey]: !prev[provider.localKey],
                            }))
                          }
                          aria-label={
                            showKeys[provider.localKey]
                              ? "Ocultar chave"
                              : "Mostrar chave"
                          }
                          aria-pressed={!!showKeys[provider.localKey]}
                        >
                          {showKeys[provider.localKey] ? (
                            <EyeOff className="w-4 h-4" aria-hidden="true" />
                          ) : (
                            <Eye className="w-4 h-4" aria-hidden="true" />
                          )}
                        </Button>
                      )}
                    </div>
                    <FieldError message={errorFor(provider, "apiKey")} />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor={`model-${provider.localKey}`}>Modelo</Label>
                    {models.length > 0 ? (
                      <Select
                        value={provider.model}
                        onValueChange={(value) => {
                          const selected = models.find((m) => m.name === value);
                          const changes: Partial<ProviderForm> = { model: value };
                          if (selected?.reasoning && !provider.reasoning) {
                            changes.reasoning = {
                              enabled: true,
                              effort: "high",
                            };
                          }
                          patch(index, changes);
                        }}
                      >
                        <SelectTrigger id={`model-${provider.localKey}`}>
                          <SelectValue placeholder="Selecione um modelo" />
                        </SelectTrigger>
                        <SelectContent>
                          {models.map((model) => (
                            <SelectItem key={model.name} value={model.name}>
                              {model.displayName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={`model-${provider.localKey}`}
                        value={provider.model}
                        onChange={(e) => patch(index, { model: e.target.value })}
                        placeholder="Ex: glm-5.3, deepseek-v4-flash"
                        className="font-mono"
                        autoComplete="off"
                      />
                    )}
                    <FieldError message={errorFor(provider, "model")} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`price-in-${provider.localKey}`}>
                      Preço por 1M tokens de entrada
                    </Label>
                    <Input
                      id={`price-in-${provider.localKey}`}
                      inputMode="decimal"
                      value={
                        priceText[priceTextKey(provider.localKey, "in")] ??
                        (provider.pricePerMTokenIn ?? "")
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        setPriceText((prev) => ({
                          ...prev,
                          [priceTextKey(provider.localKey, "in")]: raw,
                        }));
                        patch(index, { pricePerMTokenIn: parsePrice(raw) });
                      }}
                      placeholder="0.27"
                      autoComplete="off"
                    />
                    <FieldError message={errorFor(provider, "pricePerMTokenIn")} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`price-out-${provider.localKey}`}>
                      Preço por 1M tokens de saída
                    </Label>
                    <Input
                      id={`price-out-${provider.localKey}`}
                      inputMode="decimal"
                      value={
                        priceText[priceTextKey(provider.localKey, "out")] ??
                        (provider.pricePerMTokenOut ?? "")
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        setPriceText((prev) => ({
                          ...prev,
                          [priceTextKey(provider.localKey, "out")]: raw,
                        }));
                        patch(index, { pricePerMTokenOut: parsePrice(raw) });
                      }}
                      placeholder="1.10"
                      autoComplete="off"
                    />
                    <FieldError
                      message={errorFor(provider, "pricePerMTokenOut")}
                    />
                  </div>

                  <p className="md:col-span-2 text-xs text-muted-foreground">
                    Os preços são opcionais e servem apenas para exibir o custo
                    estimado de cada geração.
                  </p>

                  <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => handleLoadModels(index)}
                      disabled={
                        loadingModelsKey !== null ||
                        !(provider.apiKey || provider.hasApiKey) ||
                        !provider.baseURL
                      }
                    >
                      <RefreshCw
                        className={`w-3.5 h-3.5 ${
                          loadingModelsKey === provider.localKey
                            ? "animate-spin"
                            : ""
                        }`}
                        aria-hidden="true"
                      />
                      Carregar modelos
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => handleTest(index)}
                      disabled={
                        testingKey !== null ||
                        !(provider.apiKey || provider.hasApiKey) ||
                        !provider.baseURL
                      }
                    >
                      {testingKey === provider.localKey
                        ? "Testando…"
                        : "Testar e detectar recursos"}
                    </Button>
                    {models.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {models.length} modelos disponíveis
                      </p>
                    )}
                    {test &&
                      (test.ok ? (
                        <p className="text-xs text-success inline-flex items-center gap-1.5">
                          <Check className="w-3.5 h-3.5" aria-hidden="true" />
                          Comunicação OK em {(test.latencyMs / 1000).toFixed(1)}s
                        </p>
                      ) : (
                        <p className="text-xs text-destructive inline-flex items-center gap-1.5">
                          <X className="w-3.5 h-3.5" aria-hidden="true" />
                          Falha: {test.error}
                        </p>
                      ))}
                  </div>

                  {test?.capabilities && (
                    <ul className="md:col-span-2 text-xs text-muted-foreground space-y-1 border-l-2 border-border pl-3">
                      <li>
                        {STRUCTURED_LABEL[test.capabilities.structuredOutputs]}
                      </li>
                      <li>{reasoningLabel(test.capabilities)}</li>
                      <li>{deliveryLabel(test.capabilities)}</li>
                      <li>
                        As caixas abaixo já refletem esse resultado. Salve para
                        persistir.
                      </li>
                    </ul>
                  )}

                  {provider.type === "openai-compatible" && (
                    <div className="md:col-span-2 border-t border-border pt-4 flex flex-col md:flex-row md:items-end gap-4">
                      <div className="flex-1 space-y-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`structured-${provider.localKey}`}
                          checked={provider.supportsStructuredOutputs || false}
                          onCheckedChange={(checked) =>
                            patch(index, {
                              supportsStructuredOutputs: !!checked,
                            })
                          }
                        />
                        <Label htmlFor={`structured-${provider.localKey}`}>
                          Suporta structured output (json_schema)
                        </Label>
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`reasoning-${provider.localKey}`}
                          checked={provider.reasoning?.enabled || false}
                          onCheckedChange={(checked) =>
                            patch(index, {
                              reasoning: {
                                enabled: !!checked,
                                effort: provider.reasoning?.effort || "high",
                              },
                            })
                          }
                        />
                        <Label htmlFor={`reasoning-${provider.localKey}`}>
                          Reasoning (modelos compatíveis)
                        </Label>
                      </div>

                      </div>

                      {provider.reasoning?.enabled && (
                        <div className="space-y-2 md:w-48 md:shrink-0">
                          <Label
                            htmlFor={`reasoning-effort-${provider.localKey}`}
                          >
                            Esforço
                          </Label>
                          <Select
                            value={provider.reasoning.effort}
                            onValueChange={(value) =>
                              patch(index, {
                                reasoning: {
                                  ...provider.reasoning!,
                                  effort: value as
                                    | "low"
                                    | "medium"
                                    | "high"
                                    | "max",
                                },
                              })
                            }
                          >
                            <SelectTrigger
                              id={`reasoning-effort-${provider.localKey}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">low</SelectItem>
                              <SelectItem value="medium">medium</SelectItem>
                              <SelectItem value="high">high</SelectItem>
                              <SelectItem value="max">max</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button
        variant="secondary"
        onClick={() => {
          const provider = emptyProvider();
          setProviders((prev) => [...prev, provider]);
          setExpanded(provider.localKey);
        }}
      >
        <Plus className="w-3.5 h-3.5" aria-hidden="true" /> Adicionar provider
      </Button>

      <div className="flex items-center justify-end gap-3 pt-2">
        <p className="text-xs text-muted-foreground mr-auto inline-flex items-center gap-2">
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${
              isDirty ? "bg-primary" : "bg-muted-foreground/50"
            }`}
            aria-hidden="true"
          />
          {isDirty ? "Alterações não salvas" : "Tudo sincronizado"}
        </p>
        <Button onClick={handleSave} disabled={!isDirty || update.isLoading}>
          {update.isLoading ? "Salvando…" : "Salvar configuração"}
        </Button>
      </div>
    </div>
  );
};
